/**
 * Central credit costs configuration.
 * ALL credit amounts across the app MUST reference this file.
 * Never hardcode credit values elsewhere.
 */

import { isPremiumAiModel } from "@/lib/ai/model-preferences";
import { validateAiModel } from "@/lib/feature-gates";

export const CREDITS = {
  // --- AI model multiplier (Premium = 2x credits) ---
  premiumMultiplier: 2,

  // --- Generation costs (base; multiply by premiumMultiplier for Premium) ---
  generation: { niche: 25, plan: 50, brand: 25 },
  // --- Subscription plans (monthly credits) ---
  plans: {
    starter: { credits: 200, priceUsd: 9.90 },
    builder: { credits: 1200, priceUsd: 49.90 },
    launcher: { credits: 2500, priceUsd: 99.90 },
  },

  // --- Billing ---
  annualDiscountPercent: 0.2, // 20% off for annual billing
  cryptoBonusPercent: 0.1, // 10% extra credits for crypto payments

  // --- Credit packs (one-time purchase) ---
  packs: {
    small: { credits: 100, priceUsd: 5 },
    medium: { credits: 500, priceUsd: 25 },
    large: { credits: 1000, priceUsd: 50 },
  },

  // --- Bonuses ---
  referral: { referrer: 25, invited: 50 },
  referralMilestones: {
    first_share: 25,
    connector: 50,
    evangelist: 100,
    ambassador: 200,
    founding_circle: 500,
  } as Record<string, number>,
  shareBonus: 25, // flat credits for sharing (one-time)
  signupBonus: 50,
} as const;

/** Lowest plan price — use in marketing copy instead of hardcoding "$10/mo" */
export const STARTING_PRICE = CREDITS.plans.starter.priceUsd;

/* ─── Plan helpers (moved from stripe.ts) ─── */

export const PLANS = {
  starter: { name: "Starter", credits: CREDITS.plans.starter.credits, priceUsd: CREDITS.plans.starter.priceUsd },
  builder: { name: "Builder", credits: CREDITS.plans.builder.credits, priceUsd: CREDITS.plans.builder.priceUsd },
  launcher: { name: "Launcher", credits: CREDITS.plans.launcher.credits, priceUsd: CREDITS.plans.launcher.priceUsd },
} as const;

export type PlanKey = keyof typeof PLANS;
export type BillingInterval = "monthly" | "annual";

export function getPlanCredits(tier: PlanKey): number {
  return PLANS[tier].credits;
}

export function getPlanPrice(tier: PlanKey, interval: BillingInterval): number {
  const monthly = PLANS[tier].priceUsd;
  if (interval === "annual") {
    return Math.round(monthly * (1 - CREDITS.annualDiscountPercent)) * 12;
  }
  return monthly;
}

/**
 * Calculate effective credit cost considering AI model multiplier.
 *
 * P0-05: the multiplier is now derived from the tier-validated model — a
 * free user with `users.ai_model='premium'` is charged at the regular rate
 * (and downstream `validateAiModel`/queue gate ensures they actually run on
 * the regular model), not 2× for a privilege they don't have.
 *
 * `tier` is optional for backward-compat with legacy callers (UI surfaces
 * passing display strings without a user object); when omitted, falls back
 * to the user-provided `aiModel` directly. Pass `user.subscriptionTier`
 * wherever a server-known tier is available — see all callsites.
 */
export function getEffectiveCost(
  baseCost: number,
  aiModel?: string | null,
  tier?: string | null,
): number {
  const effectiveModel = tier !== undefined
    ? validateAiModel(aiModel, tier)
    : aiModel;
  return isPremiumAiModel(effectiveModel) ? baseCost * CREDITS.premiumMultiplier : baseCost;
}
